import { useState, useRef, useEffect, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import katex from 'katex'
import 'katex/dist/katex.min.css'
import 'katex/contrib/mhchem/mhchem'
import {
  ArrowLeft,
  ChevronsLeft,
  ChevronLeft,
  ChevronRight,
  ChevronsRight,
  AlertCircle,
  Grid3X3,
  X,
  CheckCircle2,
  Lightbulb,
  BookOpen,
  ListOrdered,
} from 'lucide-react'
import { useAuthStore } from '@/features/auth/stores/authStore'
import { useSolutionBrowserData } from '../hooks/useSolutionBrowser'
import { useSolutionAccess } from '../hooks/useActivationKey'
import ActivationKeyGate from '../components/ActivationKeyGate'
import type { SolutionViewItem } from '../types'

const IMAGE_BASE_URL = 'https://api.myneetbuddy.com/mnb-qb-images'

function renderLatexContent(htmlContent: string): string {
  if (!htmlContent) return ''

  let result = htmlContent

  result = result.replace(/\$\$([\s\S]*?)\$\$/g, (_, latex) => {
    try {
      return katex.renderToString(latex.trim(), {
        displayMode: true,
        throwOnError: false,
        trust: true,
        strict: false,
      })
    } catch {
      return `<span class="text-red-500">[Math Error: ${latex}]</span>`
    }
  })

  result = result.replace(/\$([^$\n]+?)\$/g, (_, latex) => {
    try {
      return katex.renderToString(latex.trim(), {
        displayMode: false,
        throwOnError: false,
        trust: true,
        strict: false,
      })
    } catch {
      return `<span class="text-red-500">[Math Error: ${latex}]</span>`
    }
  })

  result = result.replace(/\\\(([^)]+?)\\\)/g, (_, latex) => {
    try {
      return katex.renderToString(latex.trim(), {
        displayMode: false,
        throwOnError: false,
        trust: true,
        strict: false,
      })
    } catch {
      return `<span class="text-red-500">[Math Error: ${latex}]</span>`
    }
  })

  result = result.replace(/\\\[([\s\S]*?)\\\]/g, (_, latex) => {
    try {
      return katex.renderToString(latex.trim(), {
        displayMode: true,
        throwOnError: false,
        trust: true,
        strict: false,
      })
    } catch {
      return `<span class="text-red-500">[Math Error: ${latex}]</span>`
    }
  })

  return result
}

function processImagePlaceholders(content: string, testCode?: string): string {
  if (!content || !testCode) return content
  return content.replace(/#img\[([^\]]+)\]/g, (_, imageName) => {
    const imageUrl = `${IMAGE_BASE_URL}/${testCode}/${imageName}`
    return `<img src="${imageUrl}" alt="${imageName}" class="max-w-full h-auto my-2 rounded-lg" />`
  })
}

function render(text: string, testCode?: string): string {
  return renderLatexContent(processImagePlaceholders(text, testCode))
}

function SolutionCard({ item }: { item: SolutionViewItem }) {
  const sol = item.solutionData
  const tc = item.testCode

  const renderedExplanation = useMemo(
    () => (sol?.explanation ? render(sol.explanation, tc) : null),
    [sol?.explanation, tc]
  )

  const renderedSteps = useMemo(
    () => (sol?.steps ?? []).map((step) => render(step, tc)),
    [sol?.steps, tc]
  )

  const renderedHints = useMemo(
    () => (sol?.hints ?? []).map((hint) => render(hint, tc)),
    [sol?.hints, tc]
  )

  if (!sol) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-16 text-center">
        <AlertCircle className="w-10 h-10 text-yellow-400 mb-3" />
        <p className="text-gray-500 font-medium">No solution available for this question.</p>
      </div>
    )
  }

  return (
    <div className="p-4 space-y-5">
      {/* Question meta */}
      <div className="flex items-center gap-3 text-sm text-gray-500">
        <span className="bg-gray-100 rounded-md px-2 py-0.5">{item.sectionName}</span>
        <span className="text-gray-300">|</span>
        <span className="text-green-700 font-medium">+{item.marks}</span>
        <span className="text-gray-300">/</span>
        <span className="text-red-600 font-medium">-{item.negativeMarks}</span>
      </div>

      {/* Correct Answer */}
      <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl px-4 py-3">
        <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0" />
        <div>
          <p className="text-xs text-green-600 font-semibold uppercase tracking-wide">Correct Answer</p>
          <p className="text-lg font-bold text-green-800">{sol.answer}</p>
        </div>
      </div>

      {/* Explanation */}
      {renderedExplanation && (
        <section>
          <div className="flex items-center gap-2 mb-2">
            <BookOpen className="w-4 h-4 text-blue-500" />
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Explanation</h3>
          </div>
          <div className="h-px bg-gray-100 mb-3" />
          <div
            className="text-gray-700 leading-relaxed text-sm bg-blue-50 p-3 rounded-lg"
            dangerouslySetInnerHTML={{ __html: renderedExplanation }}
          />
        </section>
      )}

      {/* Steps */}
      {renderedSteps.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-2">
            <ListOrdered className="w-4 h-4 text-purple-500" />
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Steps</h3>
          </div>
          <div className="h-px bg-gray-100 mb-3" />
          <ol className="space-y-3">
            {renderedSteps.map((html, i) => (
              <li key={i} className="flex gap-3 text-sm text-gray-700">
                <span className="flex-shrink-0 w-5 h-5 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-xs font-bold mt-0.5">
                  {i + 1}
                </span>
                <div
                  className="leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: html }}
                />
              </li>
            ))}
          </ol>
        </section>
      )}

      {/* Hints */}
      {renderedHints.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-2">
            <Lightbulb className="w-4 h-4 text-yellow-500" />
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Hints</h3>
          </div>
          <div className="h-px bg-gray-100 mb-3" />
          <ul className="space-y-2">
            {renderedHints.map((html, i) => (
              <li key={i} className="flex gap-2 text-sm text-gray-700">
                <span className="text-yellow-500 mt-0.5 flex-shrink-0">•</span>
                <div
                  className="leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: html }}
                />
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Key Concepts */}
      {sol.keyConcepts && sol.keyConcepts.length > 0 && (
        <section>
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">Key Concepts</h3>
          <div className="h-px bg-gray-100 mb-3" />
          <div className="flex flex-wrap gap-2">
            {sol.keyConcepts.map((concept, i) => (
              <span
                key={i}
                className="inline-flex items-center bg-blue-50 text-blue-700 text-xs font-medium px-3 py-1 rounded-full border border-blue-100"
              >
                {concept}
              </span>
            ))}
          </div>
        </section>
      )}

      {/* Solution Images */}
      {sol.images && sol.images.length > 0 && (
        <section>
          <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">Images</h3>
          <div className="space-y-3">
            {sol.images.map((src, i) => (
              <img
                key={i}
                src={tc ? `${IMAGE_BASE_URL}/${tc}/${src}` : src}
                alt={`Solution image ${i + 1}`}
                className="max-w-full rounded-lg border border-gray-200"
              />
            ))}
          </div>
        </section>
      )}
    </div>
  )
}

export default function SolutionBrowserPage() {
  const { testCode } = useParams<{ testCode: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const userId = useAuthStore((s) => s.userId) ?? undefined

  const [currentIndex, setCurrentIndex] = useState(0)
  const [showQuestionPicker, setShowQuestionPicker] = useState(false)
  const pickerRef = useRef<HTMLDivElement>(null)

  const { items, isLoading, error, testName, testSubjects, assessmentLoaded } = useSolutionBrowserData(testCode ?? '', userId)
  const { data: accessData, isLoading: accessLoading } = useSolutionAccess(userId)

  const unlockedSubjects = accessData?.subjects ?? []
  const hasAccess =
    !assessmentLoaded ||
    testSubjects.length === 0 ||
    testSubjects.some((s) => unlockedSubjects.includes(s))

  const totalQuestions = items.length
  const safeIndex = Math.max(0, Math.min(currentIndex, totalQuestions - 1))
  const currentItem = totalQuestions > 0 ? items[safeIndex] : undefined

  useEffect(() => {
    if (totalQuestions > 0 && currentIndex !== safeIndex) {
      setCurrentIndex(safeIndex)
    }
  }, [currentIndex, safeIndex, totalQuestions])

  useEffect(() => {
    if (!showQuestionPicker) return
    const handler = (e: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
        setShowQuestionPicker(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [showQuestionPicker])

  const goToFirst = () => setCurrentIndex(0)
  const goToPrev = () => setCurrentIndex((i) => Math.max(0, i - 1))
  const goToNext = () => setCurrentIndex((i) => Math.min(totalQuestions - 1, i + 1))
  const goToLast = () => setCurrentIndex(totalQuestions - 1)

  if (isLoading || accessLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center space-y-3">
          <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-gray-500 text-sm">Loading solutions…</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center max-w-sm space-y-3">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto" />
          <h2 className="text-lg font-semibold text-gray-800">Failed to load solutions</h2>
          <p className="text-sm text-gray-500">
            The test code <span className="font-mono font-medium">{testCode}</span> may be invalid or
            solutions are unavailable.
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>
        </div>
      </div>
    )
  }

  if (!hasAccess && userId) {
    return (
      <ActivationKeyGate
        userId={userId}
        testSubjects={testSubjects}
        testName={testName}
        onSuccess={() => queryClient.invalidateQueries({ queryKey: ['solution-access', userId] })}
      />
    )
  }

  if (!isLoading && totalQuestions === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center max-w-sm space-y-3">
          <AlertCircle className="w-12 h-12 text-yellow-400 mx-auto" />
          <h2 className="text-lg font-semibold text-gray-800">No questions found</h2>
          <p className="text-sm text-gray-500">This test has no questions associated with it.</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 text-sm font-medium"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50 overflow-hidden">
      {/* Fixed Header */}
      <header className="flex-shrink-0 bg-white border-b border-gray-200 px-4 py-3 z-10">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium text-sm">Dashboard</span>
          </button>

          <div className="text-center">
            <h1 className="text-base font-semibold text-gray-900 leading-tight">
              {testName ?? testCode}
            </h1>
            {currentItem && (
              <p className="text-xs text-gray-500">{currentItem.sectionName}</p>
            )}
          </div>

          <div className="text-sm font-medium text-gray-500">
            Q {safeIndex + 1} / {totalQuestions}
          </div>
        </div>
      </header>

      {/* Scrollable Content */}
      <main className="flex-1 min-h-0 overflow-y-auto">
        {currentItem ? (
          <>
            <div className="px-4 pt-4">
              <h2 className="text-base font-bold text-gray-900">Question {safeIndex + 1}</h2>
            </div>
            <SolutionCard item={currentItem} />
          </>
        ) : (
          <div className="h-full flex items-center justify-center">
            <AlertCircle className="w-8 h-8 text-yellow-400" />
          </div>
        )}
      </main>

      {/* Floating Question Picker Button */}
      <button
        onClick={() => setShowQuestionPicker(true)}
        className="fixed bottom-24 right-4 w-12 h-12 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-colors flex items-center justify-center z-20"
        aria-label="Select question"
      >
        <Grid3X3 className="w-5 h-5" />
      </button>

      {/* Question Picker Modal */}
      {showQuestionPicker && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-30 p-4">
          <div
            ref={pickerRef}
            className="bg-white rounded-xl shadow-xl max-w-sm w-full max-h-[70vh] flex flex-col"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
              <h3 className="text-base font-semibold text-gray-900">Select Question</h3>
              <button
                onClick={() => setShowQuestionPicker(false)}
                className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              <div className="grid grid-cols-5 gap-2">
                {items.map((item, i) => (
                  <button
                    key={item.questionId}
                    onClick={() => {
                      setCurrentIndex(i)
                      setShowQuestionPicker(false)
                    }}
                    className={`w-full aspect-square rounded-lg text-sm font-medium transition-colors ${
                      i === safeIndex
                        ? 'bg-blue-600 text-white'
                        : item.solutionData
                        ? 'bg-green-50 text-green-700 hover:bg-green-100'
                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            </div>

            <div className="px-4 py-3 border-t border-gray-200 text-center text-xs text-gray-500">
              <span className="inline-flex items-center gap-3">
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded bg-green-50 border border-green-200 inline-block" />
                  Has solution
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded bg-gray-100 border border-gray-200 inline-block" />
                  No solution
                </span>
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Fixed Footer Navigation */}
      <nav className="flex-shrink-0 bg-white border-t border-gray-200 px-4 py-3 z-10">
        <div className="flex items-center justify-between max-w-lg mx-auto">
          <button
            onClick={goToFirst}
            disabled={safeIndex === 0}
            className="flex flex-col items-center gap-1 px-3 py-2 text-gray-600 hover:text-blue-600 disabled:text-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronsLeft className="w-6 h-6" />
            <span className="text-xs font-medium">First</span>
          </button>

          <button
            onClick={goToPrev}
            disabled={safeIndex === 0}
            className="flex flex-col items-center gap-1 px-3 py-2 text-gray-600 hover:text-blue-600 disabled:text-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-6 h-6" />
            <span className="text-xs font-medium">Prev</span>
          </button>

          <div className="text-center">
            <span className="text-lg font-bold text-gray-900">{safeIndex + 1}</span>
            <span className="text-gray-400 mx-1">/</span>
            <span className="text-lg text-gray-500">{totalQuestions}</span>
          </div>

          <button
            onClick={goToNext}
            disabled={safeIndex >= totalQuestions - 1}
            className="flex flex-col items-center gap-1 px-3 py-2 text-gray-600 hover:text-blue-600 disabled:text-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight className="w-6 h-6" />
            <span className="text-xs font-medium">Next</span>
          </button>

          <button
            onClick={goToLast}
            disabled={safeIndex >= totalQuestions - 1}
            className="flex flex-col items-center gap-1 px-3 py-2 text-gray-600 hover:text-blue-600 disabled:text-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronsRight className="w-6 h-6" />
            <span className="text-xs font-medium">Last</span>
          </button>
        </div>
      </nav>
    </div>
  )
}
